import { computed } from 'vue'
import { useActiveStatus } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useActiveStatus'
import { WINTER_SERVICE_VEHICLES_AUTOCOMPLETE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { useTaskCodebooks } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskCodebooks'
import { useTaskVehicleFacts } from '@/modules/road-maintenance-module/winter-service-v2/tasks/composables/useTaskVehicleFacts'
import { TASKS_TRANSLATION_NAMESPACE as NS } from '@/modules/road-maintenance-module/winter-service-v2/tasks/config/constants'

const COLUMNS_COUNT = 2

/**
 * The one field list behind both the task details tab and the new task screen, so the
 * two can never describe a task differently.
 *
 * It is returned already grouped into the blocks the design shows - basic data, the
 * cleaning, the vehicle, the crew, the spreading and the contractor - because those
 * groups are also what Content.vue walks to build the save payload: a field outside
 * `rows[].fields[]` saves nothing.
 *
 * What the user may change and what the vehicle decides are two separate lists here.
 * Only the task's own data is editable: the vehicle type, the drive, the throw side,
 * the contractor, the base, its responsible person, the subcontractor and the operator
 * are all configured on the vehicle and follow the picked registration - see
 * `useTaskVehicleFacts`. The spreading sits in between: the material and the quantity
 * are the user's to pick, but only within what the vehicle allows.
 */
export function useTaskFields (options) {
  // `withUnplannedToggle` is off for the screen that creates a one-off request: such a
  // task is unplanned by definition, so there is nothing to tick.
  //
  // `withVehicleFacts` is off for that same screen: what the vehicle decides is beside
  // the point there. Everywhere else the fields are drawn - empty until a registration
  // is picked, filled in by `onVehicleChange` once one is - so the user sees what the
  // vehicle brings with it while creating a task, not only after saving it.
  const { translate, withUnplannedToggle = true, withVehicleFacts = true } = options || {}

  const { statusItems } = useActiveStatus({ trans: translate })
  const {
    taskTypeItems,
    taskCategoryItems,
    surfaceTypeItems,
    cleaningTypeItems,
    spreadTypeItems
  } = useTaskCodebooks({ translate })

  const {
    rememberVehicles,
    pickVehicle,
    allowedSpreadTypeIds,
    spreadRange,
    isSpreadQuantityAllowed
  } = useTaskVehicleFacts()

  const editableField = (key, label, extra = {}) => ({
    key,
    label,
    creatable: true,
    editable: true,
    visible: true,
    ...extra
  })

  // What the vehicle's winter service configuration decides. Shown so the user sees
  // what the picked vehicle brings with it, never editable here - and `creatable`
  // stays false so it is left out of the payload entirely.
  const vehicleFactField = (key, label) => ({
    key,
    label,
    creatable: false,
    editable: false,
    visible: true
  })

  const codebookField = (key, label, items, extra = {}) => editableField(key, label, {
    type: 'autocomplete',
    items,
    autocomplete_options: {
      // A fixed, already fetched list - there is nothing to look up remotely.
      allowSearch: false
    },
    ...extra
  })

  const basicDataFields = computed(() => {
    const fields = [
      {
        key: 'status',
        label: translate(`${NS}.status`),
        type: 'autocomplete',
        items: statusItems.value,
        autocomplete_options: {
          allowSearch: false
        },
        // A brand new task has no status to pick yet - the backend starts it off as
        // active - so the field only shows up once the record exists. `creatable`
        // stays true regardless: Content.vue uses it to decide what gets sent on
        // EVERY save, edit included.
        visible: (fieldsRenderer) => !fieldsRenderer?.createMode,
        creatable: true,
        editable: true
      },
      {
        // The id is what identifies a task, and the backend assigns it - so it is
        // shown and never sent. The name beside it is the dispatcher's own label.
        key: 'id',
        label: translate(`${NS}.id`),
        creatable: false,
        editable: false,
        visible: (fieldsRenderer) => !fieldsRenderer?.createMode
      },
      editableField('name', translate(`${NS}.task_name`)),
      // The colour the task's streets are drawn in on every map that shows them. Set
      // here and nowhere else - a task is one colour, the way 1.0 has it.
      editableField('color', translate('road-maintenance/task-street.color'), {
        type: 'color'
      }),
      codebookField('type_id', translate(`${NS}.task_type`), taskTypeItems.value),
      codebookField('surface_type_id', translate(`${NS}.surface_type`), surfaceTypeItems.value),
      codebookField('category_id', translate(`${NS}.task_category`), taskCategoryItems.value)
    ]

    // A task the plan does not cover - kept off the listing unless it is asked for by
    // name, which is why it is a flag on the task rather than a category.
    if (withUnplannedToggle) {
      fields.push(editableField('is_unplanned', translate(`${NS}.is_unplanned`), {
        type: 'base-switch'
      }))
    }

    return fields
  })

  const basicDataRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.basic_data`),
    columnsCount: COLUMNS_COUNT,
    fields: basicDataFields.value
  }))

  const cleaningRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.cleaning_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      codebookField('cleaning_type_id', translate(`${NS}.cleaning_type`), cleaningTypeItems.value)
    ]
  }))

  /**
   * Picking a registration is what fills in everything the vehicle decides, and what
   * holds the spreading to the vehicle's configuration.
   *
   * The field hands back the picked id and nothing else, so the rows the autocomplete
   * answered with are kept as they arrive (`onDataReceive`) and looked up again here -
   * that is what the vehicles endpoint carries the whole configuration for.
   */
  const onVehicleChange = ({ selectedItem, updateData }) => {
    const vehicleId = selectedItem?.id ?? selectedItem
    const factValues = pickVehicle(vehicleId)
    const allowedIds = allowedSpreadTypeIds.value

    updateData?.({
      ...factValues,
      // A vehicle set up for a single material leaves nothing to pick, so it is set
      // outright; one set up for several is cleared instead, since whatever stood there
      // belonged to the previous registration. The quantity is left alone either way -
      // the range message under the field is what says it no longer fits.
      ...(allowedIds.length === 0 ? {} : { spread_type_id: allowedIds.length === 1 ? allowedIds[0] : null })
    })
  }

  const vehicleRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.vehicle_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      // A task may only point at a vehicle set up for the winter service - the task
      // endpoints refuse anything else - so only those are offered.
      editableField('vehicle_id', translate(`${NS}.registration`), {
        type: 'autocomplete',
        autocomplete_options: {
          ...WINTER_SERVICE_VEHICLES_AUTOCOMPLETE,
          onDataReceive: ({ normalizedData }) => rememberVehicles(normalizedData)
        },
        onChange: onVehicleChange
      }),
      ...(withVehicleFacts
        ? [
            vehicleFactField('vehicle_type', translate(`${NS}.vehicle_type`)),
            vehicleFactField('drive_type', translate(`${NS}.drive_type`))
          ]
        : [])
    ]
  }))

  const crewRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.crew_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      editableField('driver_id', translate(`${NS}.driver`), {
        type: 'autocomplete',
        autocomplete_options: {
          module: 'people',
          route: 'people/autocomplete'
        }
      }),
      editableField('worker_count', translate(`${NS}.worker_count`), {
        field_type: 'number'
      })
    ]
  }))

  // What the picked vehicle is allowed to spread. A vehicle with no material of its
  // own puts no limit on the task, which is why the whole codebook stands in for it.
  const allowedSpreadTypeItems = computed(() => {
    const allowedIds = allowedSpreadTypeIds.value

    if (!allowedIds.length) {
      return spreadTypeItems.value
    }

    return spreadTypeItems.value.filter(item => allowedIds.includes(Number(item.id)))
  })

  /**
   * The range the picked vehicle can carry, as the message the design wants under the
   * field - red, because it is rendered as a validation error rather than a hint.
   *
   * Built per range rather than once: the field re-validates when its rules change, and
   * a rule that never changes identity would leave a quantity that was fine for the
   * previous registration standing unmarked under the new one.
   */
  const buildSpreadQuantityRule = ({ min, max }) => (value) => {
    if (isSpreadQuantityAllowed(value)) {
      return true
    }

    return translate(`${NS}.spread_quantity_out_of_range`)
      .replace(':min', min === null ? '0' : String(min))
      .replace(':max', max === null ? '-' : String(max))
  }

  const spreadingRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.spreading_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      codebookField('spread_type_id', translate(`${NS}.spread_type`), allowedSpreadTypeItems.value),
      editableField('spread_quantity', translate(`${NS}.spread_quantity`), {
        field_type: 'number',
        props: {
          rules: [buildSpreadQuantityRule(spreadRange.value)]
        }
      }),
      ...(withVehicleFacts
        ? [vehicleFactField('throw_side', translate(`${NS}.throw_side`))]
        : [])
    ]
  }))

  // All of it comes from the vehicle's configuration - which subcontractor owns it,
  // which base it answers to, who operates it. The task only points at the vehicle.
  const contractorRow = computed(() => ({
    type: 'fields',
    title: translate(`${NS}.contractor_data`),
    columnsCount: COLUMNS_COUNT,
    fields: [
      vehicleFactField('ownership', translate(`${NS}.contractor`)),
      vehicleFactField('base_name', translate(`${NS}.responsible_base`)),
      vehicleFactField('base_responsible_person', translate(`${NS}.base_responsible_person`)),
      vehicleFactField('subcontractor_name', translate(`${NS}.subcontractor`)),
      vehicleFactField('operator_name', translate(`${NS}.operator`))
    ]
  }))

  const rows = computed(() => ([
    basicDataRow.value,
    cleaningRow.value,
    vehicleRow.value,
    crewRow.value,
    spreadingRow.value,
    // Nothing but vehicle facts, so the whole block goes with them.
    ...(withVehicleFacts ? [contractorRow.value] : [])
  ]))

  return {
    rows
  }
}
